import { IconButton, StandardTextFieldProps, TextField, TextFieldProps } from '@mui/material';
import React, { ReactNode, useMemo, useState } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import TooltipHardened from '../TooltipHardened';
import { InputActionProps, InputAdditions, InputCollapsibleProps } from './InputAdditions';

interface Props {
  label: string;
  numeric?: boolean;
  tooltip?: ReactNode;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  action?: InputActionProps;
  collapsible?: InputCollapsibleProps;
}

export const StyledTextField = ({
  numeric,
  tooltip,
  endAdornment,
  startAdornment,
  type,
  inputProps,
  InputProps,
  action,
  collapsible,
  ...props
}: Props & TextFieldProps): React.JSX.Element => {
  const [isPasswordHidden, setPasswordHidden] = useState<boolean>(true);

  const InputPropsExtended = useMemo((): TextFieldProps['InputProps'] => {
    if (InputProps === undefined) {
      return {
        endAdornment:
          type === 'password' ? (
            <TooltipHardened placement={'left'} tooltip={isPasswordHidden ? 'show' : 'hide'}>
              <IconButton onClick={() => setPasswordHidden(!isPasswordHidden)}>
                {isPasswordHidden ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </TooltipHardened>
          ) : (
            endAdornment
          ),
        startAdornment,
      };
    }

    return InputProps;
  }, [isPasswordHidden, InputProps, endAdornment, startAdornment]);

  // enforce digit-only input
  const numericInputProps: StandardTextFieldProps['inputProps'] = numeric
    ? { inputMode: 'numeric', pattern: '[0-9]*' }
    : undefined;
  const { InputLabelProps, ...restProps } = props;

  return (
    <InputAdditions action={action} collapsible={collapsible}>
      <TooltipHardened arrow tooltip={tooltip}>
        <TextField
          fullWidth
          {...restProps}
          variant={'outlined'}
          type={type === 'password' && !isPasswordHidden ? 'text' : type}
          InputProps={InputPropsExtended}
          inputProps={inputProps ?? numericInputProps}
          InputLabelProps={InputLabelProps}
        />
      </TooltipHardened>
    </InputAdditions>
  );
};
